import type { SentryLogLevel, SentryLogProps } from './types';

interface BuildSentryEventArgs extends SentryLogProps {
	level: SentryLogLevel;
	packageName: string;
}

interface ExceptionCapture {
	error: Error;
	kind: 'exception';
}

interface MessageCapture {
	kind: 'message';
	message: string;
}

type SentryCapture = ExceptionCapture | MessageCapture;

export interface BuiltSentryEvent {
	capture: SentryCapture;
	fingerprint: string[];
	tags: Record<string, string>;
}

/**
 * Converts the public logging shape into a Sentry capture instruction plus searchable metadata.
 * Error logs preserve a caller-provided stack or create a synthetic exception; warning and info
 * logs remain messages. Native errors retain Sentry's stack grouping, while all other events use
 * the controlled taxonomy for deterministic grouping.
 */
export const buildSentryEvent = ({
	packageName,
	feature,
	element,
	operation,
	category,
	error,
	message,
	errorCode,
	traceId,
	requestId,
	product,
	level,
}: BuildSentryEventArgs): BuiltSentryEvent => {
	const code = errorCode == null ? undefined : String(errorCode);
	const syntheticMessage = message ?? `[${feature}] ${operation} failed${code ? `: ${code}` : ''}`;
	const hasCallerErrorStack = level === 'error' && error instanceof Error && Boolean(error.stack);
	const capture: SentryCapture =
		level === 'error'
			? {
					error: hasCallerErrorStack ? error : new Error(syntheticMessage),
					kind: 'exception',
				}
			: {
					kind: 'message',
					message: syntheticMessage,
				};

	const fingerprint = hasCallerErrorStack
		? ['{{ default }}', feature, element]
		: [feature, element, operation, category, ...(code ? [code] : [])];

	const tags: Record<string, string> = {
		packageName,
		search: 'true',
		feature,
		element,
		operation,
		category,
	};

	if (code) {
		tags.errorCode = code;
	}
	if (traceId) {
		tags.traceId = traceId;
	}
	if (requestId) {
		tags.requestId = requestId;
	}
	if (product) {
		tags.product = product;
	}

	return {
		capture,
		fingerprint,
		tags,
	};
};
